import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { dataHistoryApi } from './api'
import type { DeleteHistoryPayload, HistorySource } from './types'

export function useHistory(source: HistorySource) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'history', source),
    queryFn: () => dataHistoryApi.list(source),
  })
}

export function useHistoryTransactions(source: HistorySource | null, id: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'history', 'transactions', source ?? 'none', id ?? 'none'),
    queryFn: () => dataHistoryApi.transactionsByHistory(source!, id!),
    enabled: !!source && !!id,
  })
}

export function useRequestHistoryDeleteCode() {
  return useMutation({
    mutationFn: () => dataHistoryApi.requestDeleteCode(),
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'history') })
}

export function useDeleteHistoryOne() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: DeleteHistoryPayload }) =>
      dataHistoryApi.deleteOne(id, payload),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useDeleteHistorySource() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({
      source,
      payload,
    }: {
      source: HistorySource
      payload: DeleteHistoryPayload
    }) => dataHistoryApi.deleteSource(source, payload),
    onSuccess: () => invalidate(qc, tenantId),
  })
}
